// redux/addressSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Fetch all addresses
export const fetchAddresses = createAsyncThunk(
  'address/fetchAddresses',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(
        'https://evercorenutrition.com/ev_admin/api/customer/addresses/',
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch addresses');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add new address
export const addAddress = createAsyncThunk(
  'address/addAddress',
  async (addressData, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem('authToken');

      const response = await fetch(
        'https://evercorenutrition.com/ev_admin/api/customer/addresses/',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            street: addressData.line1,
            city: addressData.city,
            state: addressData.state,
            zip_code: addressData.zip,
            country: addressData.country,
            is_primary: false
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add address');
      }

      const data = await response.json();
      
      // Auto-refresh addresses after adding
      dispatch(fetchAddresses());
      
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Edit address
export const editAddress = createAsyncThunk(
  'address/editAddress',
  async ({ addressId, addressData }, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem('authToken');

      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/customer/addresses/${addressId}/`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            street: addressData.line1,
            city: addressData.city,
            state: addressData.state,
            zip_code: addressData.zip,
            country: addressData.country,
            is_primary: false
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update address');
      }

      const data = await response.json();
      
      // Auto-refresh addresses after editing
      dispatch(fetchAddresses());
      
      return { addressId, data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Delete address
export const deleteAddress = createAsyncThunk(
  'address/deleteAddress',
  async (addressId, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem('authToken');

      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/customer/addresses/${addressId}/`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete address');
      }

      // Auto-refresh addresses after deleting
      dispatch(fetchAddresses());
      
      return addressId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  addresses: [],
  loading: false,
  error: null,
  successMessage: null,
  actionLoading: false
};

const addressSlice = createSlice({
  name: 'address',
  initialState,
  reducers: {
    clearAddressMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch addresses
      .addCase(fetchAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.loading = false;
        // Check if the response has a success flag and addresses array
        if (action.payload?.success && Array.isArray(action.payload.addresses)) {
          // Convert API format to frontend format
          state.addresses = action.payload.addresses.map(addr => ({
            id: addr.id,
            line1: addr.street || '',
            city: addr.city || '',
            state: addr.state || '',
            country: addr.country || 'India',
            zip: addr.zip_code || '',
            label: addr.label || 'Home', // Add default label if not provided
            is_default_billing: addr.is_primary || false,
            is_default_shipping: addr.is_primary || false,
          }));
        } else {
          state.addresses = [];
        }
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.addresses = [];
      })

      // Add address
      .addCase(addAddress.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(addAddress.fulfilled, (state) => {
        state.actionLoading = false;
        state.successMessage = 'Address added successfully';
      })
      .addCase(addAddress.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // Edit address
      .addCase(editAddress.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(editAddress.fulfilled, (state) => {
        state.actionLoading = false;
        state.successMessage = 'Address updated successfully';
      })
      .addCase(editAddress.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // Delete address
      .addCase(deleteAddress.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.successMessage = 'Address deleted successfully';
        // Remove immediately from state so UI updates without waiting for re-fetch
        state.addresses = state.addresses.filter(addr => addr.id !== action.payload);
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearAddressMessages } = addressSlice.actions;
export default addressSlice.reducer;